/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 * Page Reports Constructor component
 *
 * @author Evgeniy Naumov
 * @version 1.22.0
 * @since 1.22.0
 */

import { FC } from 'react';

import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { Button, LinkBlock } from '@components/common';
import ArrowTooltip from '@components/common/Tooltip/Tooltip';
import { IPageReportsConstructorTranslation } from '@modules/internationalization/types';
import { RoutePath } from '@routes/constants/routesConstants';

import { EConstructorItem, EConstructorReportsNew } from '../constants';
import { reportsConstructorSelectedItemSelector } from '../selector';
import { setCreateTemplateTooltip, setOrderReportTooltip } from '../utils/setFooterTooltip';
import {
    ConstructorFooterControlsItemStyled,
    ConstructorFooterControlsLinkStyled,
    ConstructorFooterControlsStyled,
    ConstructorFooterStyled,
} from './styled/ConstructorFooterStyled';

interface IReportsConstructionFooterProps {
    translation: IPageReportsConstructorTranslation;
    subscribersInBasket: number;
}

const ReportsConstructorFooter: FC<IReportsConstructionFooterProps> = ({ translation, subscribersInBasket }) => {
    const history = useHistory();
    const selectedItem = useSelector(reportsConstructorSelectedItemSelector);

    const isOrderReportDisabled =
        (!subscribersInBasket && selectedItem?.reportType !== EConstructorReportsNew.REPORT_CUSTOM_CORP_CLIENTS) ||
        !selectedItem;

    const isCreateTemplateDisabled =
        !selectedItem ||
        !EConstructorReportsNew[selectedItem.reportType as EConstructorReportsNew] ||
        selectedItem.itemType === EConstructorItem.TEMPLATE;

    const handleCreateTemplateClick = () => {
        history.push(history.location.pathname + RoutePath.wizardOrderTemplateCreate, {
            report: selectedItem,
            mode: 'TEMPLATE_CREATE',
        });
    };

    const handleOrderReportClick = () => {
        if (
            (selectedItem && EConstructorReportsNew[selectedItem.reportType as EConstructorReportsNew]) ||
            selectedItem?.itemType === EConstructorItem.TEMPLATE
        ) {
            history.push(history.location.pathname + RoutePath.wizardOrderReport, { report: selectedItem });
        }
    };

    const handleOrderListClick = () => {
        history.push(RoutePath.orders);
    };

    return (
        <ConstructorFooterStyled>
            <ConstructorFooterControlsStyled>
                <ConstructorFooterControlsLinkStyled>
                    <LinkBlock onClick={handleOrderListClick}>{translation.footer.orderList}</LinkBlock>
                </ConstructorFooterControlsLinkStyled>
                <ConstructorFooterControlsItemStyled>
                    <ArrowTooltip
                        disableHoverListener={!isCreateTemplateDisabled}
                        title={setCreateTemplateTooltip(translation, selectedItem)}
                    >
                        <span>
                            <Button
                                disabled={isCreateTemplateDisabled}
                                kind="mediumNormal"
                                type="submit"
                                onClick={handleCreateTemplateClick}
                            >
                                {translation.footer.createTemplate}
                            </Button>
                        </span>
                    </ArrowTooltip>
                </ConstructorFooterControlsItemStyled>
                <ConstructorFooterControlsItemStyled>
                    <ArrowTooltip
                        disableHoverListener={!isOrderReportDisabled}
                        title={setOrderReportTooltip(translation, selectedItem, subscribersInBasket)}
                    >
                        <span>
                            <Button
                                disabled={isOrderReportDisabled}
                                kind="mediumNormal"
                                type="submit"
                                onClick={handleOrderReportClick}
                            >
                                {translation.footer.orederReport}
                            </Button>
                        </span>
                    </ArrowTooltip>
                </ConstructorFooterControlsItemStyled>
            </ConstructorFooterControlsStyled>
        </ConstructorFooterStyled>
    );
};

export default ReportsConstructorFooter;
